import { createFileRoute } from '@tanstack/react-router';
import { formatDistanceToNowStrict } from 'date-fns';
import {
    AlertTriangle,
    ChevronRight,
    Database,
    Eye,
    FlaskConical,
    Loader2,
    Pause,
    Pencil,
    Play,
    Plus,
    RotateCcw,
    ShieldCheck,
} from 'lucide-react';
import React, { type FormEvent, useEffect, useMemo, useState } from 'react';

import { Badge } from '@apps/web/components/ui/badge';
import { Button } from '@apps/web/components/ui/button';
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from '@apps/web/components/ui/card';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@apps/web/components/ui/dialog';
import { Input } from '@apps/web/components/ui/input';
import { Label } from '@apps/web/components/ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@apps/web/components/ui/select';
import { Spinner } from '@apps/web/components/ui/spinner';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@apps/web/components/ui/table';
import { Textarea } from '@apps/web/components/ui/textarea';
import { toast } from '@apps/web/lib/app_notifications';
import {
    createDocumentIngestionPipeline,
    type DocumentIngestionAction,
    runDocumentIngestionAction,
    updateDocumentIngestionPipeline,
    useDocumentIngestionCatalog,
    useDocumentIngestionPipelines,
    useDocumentIngestionRun,
    useDocumentIngestionRuns,
} from '@apps/web/swr_data_fetching/document_ingestion';
import { ApiError, type components } from '@libraries/typescript/api_client';

type Pipeline = components['schemas']['DocumentIngestionPipelineRead'];
type PipelineStatus = components['schemas']['DocumentIngestionPipelineStatus'];
type Run = components['schemas']['DocumentIngestionRunRead'];
type RunStatus = components['schemas']['DocumentIngestionRunStatus'];
type SourceAccount =
    components['schemas']['DocumentIngestionSourceAccountRead'];

export const Route = createFileRoute('/_app/admin/document-ingestion')({
    component: DocumentIngestionPage,
    head: () => ({
        meta: [{ title: 'Document Ingestion | Phoebe Admin' }],
    }),
});

const STATUS_VARIANTS: Record<
    PipelineStatus,
    'default' | 'outline' | 'secondary'
> = {
    active: 'default',
    draft: 'outline',
    paused: 'secondary',
};

const RUN_VARIANTS: Record<
    RunStatus,
    'default' | 'destructive' | 'outline' | 'secondary'
> = {
    cancelled: 'secondary',
    failed: 'destructive',
    partial: 'outline',
    queued: 'secondary',
    running: 'outline',
    succeeded: 'default',
};

const ACTION_COPY: Record<
    DocumentIngestionAction,
    {
        readonly title: string;
        readonly description: string;
        readonly button: string;
    }
> = {
    preview: {
        title: 'Run metadata-only preview?',
        description:
            'This lists document metadata and evaluates the configured scope. It cannot download a PDF or invoke Textract.',
        button: 'Queue preview',
    },
    canary: {
        title: 'Process one canary document?',
        description:
            'This may download and OCR one eligible document. Identity checks must pass before Phoebe writes a care-plan supplement.',
        button: 'Queue canary',
    },
    activate: {
        title: 'Activate scheduled ingestion?',
        description:
            'A successful preview and one-document canary are required for this exact configuration version. Activation enables the saved schedule.',
        button: 'Activate pipeline',
    },
    'run-now': {
        title: 'Run this pipeline now?',
        description:
            'The active configuration and document cap will be snapshotted into a durable manual run.',
        button: 'Queue run',
    },
    pause: {
        title: 'Pause new acquisition?',
        description:
            'This cancels queued scheduled work and stops future schedules. Existing care-plan supplements remain available to Phoebe.',
        button: 'Pause pipeline',
    },
    resume: {
        title: 'Resume scheduled ingestion?',
        description:
            'The pipeline will return to its saved schedule using the current saved configuration.',
        button: 'Resume pipeline',
    },
};

type FormState = {
    readonly sourceAccountId: string;
    readonly scopeType: 'all_active' | 'client_classes';
    readonly clientClasses: string;
    readonly selectorType: 'any_pdf' | 'filename_pattern';
    readonly filenamePattern: string;
    readonly frequency: 'daily' | 'weekly';
    readonly weekday: string;
    readonly hour: string;
    readonly minute: string;
    readonly maxDocuments: string;
};

const EMPTY_FORM: FormState = {
    sourceAccountId: '',
    scopeType: 'all_active',
    clientClasses: '',
    selectorType: 'filename_pattern',
    filenamePattern: '',
    frequency: 'daily',
    weekday: '0',
    hour: '9',
    minute: '20',
    maxDocuments: '20',
};

type FormDialogState =
    | { readonly kind: 'create' }
    | { readonly kind: 'edit'; readonly pipeline: Pipeline }
    | null;

type ActionDialogState = {
    readonly action: DocumentIngestionAction;
    readonly pipeline: Pipeline;
} | null;

function getErrorMessage(error: unknown, fallback: string): string {
    if (
        error instanceof ApiError &&
        typeof error.body === 'object' &&
        error.body !== null &&
        'detail' in error.body &&
        typeof error.body.detail === 'string'
    ) {
        return error.body.detail;
    }
    return fallback;
}

function relativeTime(value: string | null | undefined): string {
    if (!value) {
        return 'Never';
    }
    return formatDistanceToNowStrict(new Date(value), { addSuffix: true });
}

function scopeLabel(pipeline: Pipeline): string {
    if (pipeline.client_scope.type === 'all_active') {
        return 'All active clients';
    }
    return `${pipeline.client_scope.values.length} client class${
        pipeline.client_scope.values.length === 1 ? '' : 'es'
    }`;
}

function selectorLabel(pipeline: Pipeline): string {
    return pipeline.document_selector.type === 'any_pdf'
        ? 'Newest PDF (any filename)'
        : 'Newest matching filename';
}

function runSummary(run: Run): string {
    if (run.trigger === 'preview') {
        return `${run.selected_count} selected · no downloads`;
    }
    return `${run.written_count} written · ${run.refused_count} refused · ${run.failed_count} failed`;
}

function PipelineFormDialog({
    state,
    sourceAccounts,
    minimumCap,
    maximumCap,
    onClose,
    onSaved,
}: {
    readonly state: FormDialogState;
    readonly sourceAccounts: ReadonlyArray<SourceAccount>;
    readonly minimumCap: number;
    readonly maximumCap: number;
    readonly onClose: () => void;
    readonly onSaved: () => Promise<unknown>;
}) {
    const [form, setForm] = useState<FormState>(EMPTY_FORM);
    const [isSaving, setIsSaving] = useState(false);
    const defaultSourceAccountId = sourceAccounts[0]?.id ?? '';
    const defaultFrequency =
        sourceAccounts[0]?.source_type === 'wellsky' ? 'weekly' : 'daily';

    useEffect(() => {
        if (!state) {
            return;
        }
        if (state.kind === 'create') {
            setForm({
                ...EMPTY_FORM,
                sourceAccountId: defaultSourceAccountId,
                frequency: defaultFrequency,
            });
            return;
        }
        const { pipeline } = state;
        setForm({
            sourceAccountId: pipeline.data_source_account_id,
            scopeType: pipeline.client_scope.type,
            clientClasses:
                pipeline.client_scope.type === 'client_classes'
                    ? pipeline.client_scope.values.join('\n')
                    : '',
            selectorType: pipeline.document_selector.type,
            filenamePattern:
                pipeline.document_selector.type === 'filename_pattern'
                    ? pipeline.document_selector.pattern
                    : '',
            frequency: pipeline.schedule.type,
            weekday: String(
                pipeline.schedule.type === 'weekly'
                    ? pipeline.schedule.weekday
                    : 0,
            ),
            hour: String(pipeline.schedule.hour),
            minute: String(pipeline.schedule.minute),
            maxDocuments: String(pipeline.max_documents_per_run),
        });
    }, [defaultSourceAccountId, defaultFrequency, state]);

    if (!state) {
        return null;
    }

    const selectedAccount = sourceAccounts.find(
        (account) => account.id === form.sourceAccountId,
    );

    const handleSubmit = async (event: FormEvent) => {
        event.preventDefault();
        const clientClasses = form.clientClasses
            .split(/[\n,]/)
            .map((value) => value.trim())
            .filter(Boolean);
        const maxDocuments = Number(form.maxDocuments);
        const hour = Number(form.hour);
        const minute = Number(form.minute);

        if (!selectedAccount) {
            toast.error('Select a source account', {
                persistNotification: false,
            });
            return;
        }
        if (form.scopeType === 'client_classes' && clientClasses.length === 0) {
            toast.error('Add at least one AxisCare client class', {
                persistNotification: false,
            });
            return;
        }
        if (
            form.selectorType === 'filename_pattern' &&
            form.filenamePattern.trim().length === 0
        ) {
            toast.error('Add a whole-filename regex pattern', {
                persistNotification: false,
            });
            return;
        }
        if (
            !Number.isInteger(maxDocuments) ||
            maxDocuments < minimumCap ||
            maxDocuments > maximumCap ||
            !Number.isInteger(hour) ||
            hour < 0 ||
            hour > 23 ||
            !Number.isInteger(minute) ||
            minute < 0 ||
            minute > 59
        ) {
            toast.error('Use a valid UTC time and document cap', {
                persistNotification: false,
            });
            return;
        }

        const clientScope =
            form.scopeType === 'all_active'
                ? ({ type: 'all_active' } as const)
                : ({ type: 'client_classes', values: clientClasses } as const);
        const documentSelector =
            form.selectorType === 'any_pdf'
                ? ({
                      type: 'any_pdf',
                      selection: 'newest_eligible',
                  } as const)
                : ({
                      type: 'filename_pattern',
                      pattern: form.filenamePattern.trim(),
                      selection: 'newest_eligible',
                  } as const);
        const schedule =
            form.frequency === 'weekly'
                ? {
                      type: 'weekly' as const,
                      weekday: Number(form.weekday),
                      hour,
                      minute,
                  }
                : { type: 'daily' as const, hour, minute };

        setIsSaving(true);
        try {
            if (state.kind === 'create') {
                await createDocumentIngestionPipeline({
                    organization_id: selectedAccount.organization_id,
                    data_source_account_id: selectedAccount.id,
                    client_scope: clientScope,
                    document_selector: documentSelector,
                    schedule,
                    max_documents_per_run: maxDocuments,
                });
            } else {
                await updateDocumentIngestionPipeline(state.pipeline.id, {
                    version: state.pipeline.version,
                    client_scope: clientScope,
                    document_selector: documentSelector,
                    schedule,
                    max_documents_per_run: maxDocuments,
                });
            }
            await onSaved();
            toast.success(
                state.kind === 'create'
                    ? 'Draft pipeline created'
                    : 'Pipeline configuration updated',
                { persistNotification: false },
            );
            onClose();
        } catch (error) {
            toast.error(getErrorMessage(error, 'Failed to save pipeline'), {
                persistNotification: false,
            });
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Dialog open={true} onOpenChange={(open) => !open && onClose()}>
            <DialogContent className="sm:max-w-2xl">
                <DialogHeader>
                    <DialogTitle>
                        {state.kind === 'create'
                            ? 'Create document pipeline'
                            : `Edit ${state.pipeline.organization_name}`}
                    </DialogTitle>
                    <DialogDescription>
                        Every pipeline is live-only and bound to one explicit
                        source account. New and changed configurations must pass
                        preview and canary checks before activation.
                    </DialogDescription>
                </DialogHeader>
                <form className="grid gap-5" onSubmit={handleSubmit}>
                    <div className="grid gap-2">
                        <Label htmlFor="document-source-account">
                            Source account
                        </Label>
                        <Select
                            value={form.sourceAccountId}
                            onValueChange={(sourceAccountId) =>
                                setForm((current) => ({
                                    ...current,
                                    sourceAccountId,
                                    scopeType: 'all_active',
                                    frequency:
                                        sourceAccounts.find(
                                            (account) =>
                                                account.id === sourceAccountId,
                                        )?.source_type === 'wellsky'
                                            ? 'weekly'
                                            : 'daily',
                                }))
                            }
                            disabled={state.kind === 'edit'}
                        >
                            <SelectTrigger id="document-source-account">
                                <SelectValue placeholder="Select an account" />
                            </SelectTrigger>
                            <SelectContent>
                                {sourceAccounts.map((account) => (
                                    <SelectItem
                                        key={account.id}
                                        value={account.id}
                                        disabled={!account.sync_enabled}
                                    >
                                        {account.organization_name} —{' '}
                                        {account.source_label}
                                        {!account.sync_enabled
                                            ? ' (paused)'
                                            : ''}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="grid gap-4 sm:grid-cols-2">
                        <div className="grid gap-2">
                            <Label htmlFor="document-scope">Client scope</Label>
                            <Select
                                value={form.scopeType}
                                onValueChange={(scopeType) =>
                                    setForm((current) => ({
                                        ...current,
                                        scopeType:
                                            scopeType as FormState['scopeType'],
                                    }))
                                }
                            >
                                <SelectTrigger id="document-scope">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all_active">
                                        All active clients
                                    </SelectItem>
                                    <SelectItem
                                        value="client_classes"
                                        disabled={
                                            selectedAccount?.source_type ===
                                            'wellsky'
                                        }
                                    >
                                        Selected client classes
                                    </SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="grid gap-2">
                            <Label htmlFor="document-selector">
                                Document selector
                            </Label>
                            <Select
                                value={form.selectorType}
                                onValueChange={(selectorType) =>
                                    setForm((current) => ({
                                        ...current,
                                        selectorType:
                                            selectorType as FormState['selectorType'],
                                    }))
                                }
                            >
                                <SelectTrigger id="document-selector">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="filename_pattern">
                                        Filename pattern
                                    </SelectItem>
                                    <SelectItem value="any_pdf">
                                        Any PDF
                                    </SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    {form.scopeType === 'client_classes' && (
                        <div className="grid gap-2">
                            <Label htmlFor="document-client-classes">
                                AxisCare client classes
                            </Label>
                            <Textarea
                                id="document-client-classes"
                                value={form.clientClasses}
                                onChange={(event) =>
                                    setForm((current) => ({
                                        ...current,
                                        clientClasses: event.target.value,
                                    }))
                                }
                                placeholder="One exact class per line"
                                rows={3}
                            />
                        </div>
                    )}
                    {form.selectorType === 'filename_pattern' ? (
                        <div className="grid gap-2">
                            <Label htmlFor="document-filename-pattern">
                                Whole-filename regex
                            </Label>
                            <Input
                                id="document-filename-pattern"
                                value={form.filenamePattern}
                                onChange={(event) =>
                                    setForm((current) => ({
                                        ...current,
                                        filenamePattern: event.target.value,
                                    }))
                                }
                                placeholder="(?i).*service questionnaire.*\\.pdf"
                                className="font-mono"
                            />
                        </div>
                    ) : (
                        <div className="flex gap-3 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-950 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-100">
                            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                            <p>
                                Any PDF is a broad selector. Identity checks can
                                refuse the wrong patient, but they cannot prove
                                the document is the intended form. Use only when
                                the source account is operationally constrained.
                            </p>
                        </div>
                    )}
                    <div className="grid gap-4 sm:grid-cols-2">
                        <div className="grid gap-2">
                            <Label htmlFor="document-frequency">
                                Sync frequency
                            </Label>
                            <Select
                                value={form.frequency}
                                onValueChange={(frequency) =>
                                    setForm((current) => ({
                                        ...current,
                                        frequency:
                                            frequency as FormState['frequency'],
                                    }))
                                }
                            >
                                <SelectTrigger id="document-frequency">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="weekly">
                                        Weekly
                                    </SelectItem>
                                    <SelectItem value="daily">Daily</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        {form.frequency === 'weekly' && (
                            <div className="grid gap-2">
                                <Label htmlFor="document-weekday">
                                    UTC weekday
                                </Label>
                                <Select
                                    value={form.weekday}
                                    onValueChange={(weekday) =>
                                        setForm((current) => ({
                                            ...current,
                                            weekday,
                                        }))
                                    }
                                >
                                    <SelectTrigger id="document-weekday">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {[
                                            'Monday',
                                            'Tuesday',
                                            'Wednesday',
                                            'Thursday',
                                            'Friday',
                                            'Saturday',
                                            'Sunday',
                                        ].map((day, index) => (
                                            <SelectItem
                                                key={day}
                                                value={String(index)}
                                            >
                                                {day}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        )}
                    </div>
                    <div className="grid gap-4 sm:grid-cols-3">
                        <div className="grid gap-2">
                            <Label htmlFor="document-hour">UTC hour</Label>
                            <Input
                                id="document-hour"
                                type="number"
                                min={0}
                                max={23}
                                value={form.hour}
                                onChange={(event) =>
                                    setForm((current) => ({
                                        ...current,
                                        hour: event.target.value,
                                    }))
                                }
                            />
                        </div>
                        <div className="grid gap-2">
                            <Label htmlFor="document-minute">UTC minute</Label>
                            <Input
                                id="document-minute"
                                type="number"
                                min={0}
                                max={59}
                                value={form.minute}
                                onChange={(event) =>
                                    setForm((current) => ({
                                        ...current,
                                        minute: event.target.value,
                                    }))
                                }
                            />
                        </div>
                        <div className="grid gap-2">
                            <Label htmlFor="document-cap">
                                Documents per run
                            </Label>
                            <Input
                                id="document-cap"
                                type="number"
                                min={minimumCap}
                                max={maximumCap}
                                value={form.maxDocuments}
                                onChange={(event) =>
                                    setForm((current) => ({
                                        ...current,
                                        maxDocuments: event.target.value,
                                    }))
                                }
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={onClose}
                        >
                            Cancel
                        </Button>
                        <Button type="submit" disabled={isSaving}>
                            {isSaving && <Spinner className="mr-2" size="sm" />}
                            Save draft
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}

function ActionDialog({
    state,
    onClose,
    onComplete,
}: {
    readonly state: ActionDialogState;
    readonly onClose: () => void;
    readonly onComplete: (pipelineId: string) => Promise<void>;
}) {
    const [isRunning, setIsRunning] = useState(false);
    if (!state) {
        return null;
    }
    const copy = ACTION_COPY[state.action];

    const handleAction = async () => {
        setIsRunning(true);
        try {
            await runDocumentIngestionAction(state.pipeline.id, state.action);
            await onComplete(state.pipeline.id);
            toast.success(`${copy.button} succeeded`, {
                persistNotification: false,
            });
            onClose();
        } catch (error) {
            toast.error(
                getErrorMessage(
                    error,
                    `Failed to ${copy.button.toLowerCase()}`,
                ),
                {
                    persistNotification: false,
                },
            );
        } finally {
            setIsRunning(false);
        }
    };

    return (
        <Dialog open={true} onOpenChange={(open) => !open && onClose()}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>{copy.title}</DialogTitle>
                    <DialogDescription>{copy.description}</DialogDescription>
                </DialogHeader>
                <div className="bg-muted rounded-md p-3 text-sm">
                    <span className="font-medium">
                        {state.pipeline.organization_name}
                    </span>{' '}
                    · {state.pipeline.source_label} · configuration v
                    {state.pipeline.version}
                </div>
                <DialogFooter>
                    <Button type="button" variant="outline" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button
                        type="button"
                        variant={
                            state.action === 'pause' ? 'destructive' : 'default'
                        }
                        disabled={isRunning}
                        onClick={handleAction}
                    >
                        {isRunning && <Spinner className="mr-2" size="sm" />}
                        {copy.button}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function PipelineActions({
    pipeline,
    onAction,
    onEdit,
}: {
    readonly pipeline: Pipeline;
    readonly onAction: (action: DocumentIngestionAction) => void;
    readonly onEdit: () => void;
}) {
    return (
        <div className="flex min-w-[270px] flex-wrap justify-end gap-2">
            {pipeline.status !== 'active' && (
                <Button size="sm" variant="outline" onClick={onEdit}>
                    <Pencil className="mr-1 h-3.5 w-3.5" /> Edit
                </Button>
            )}
            {pipeline.status === 'draft' && (
                <React.Fragment>
                    <Button
                        size="sm"
                        variant="outline"
                        onClick={() => onAction('preview')}
                    >
                        <Eye className="mr-1 h-3.5 w-3.5" /> Preview
                    </Button>
                    <Button
                        size="sm"
                        variant="outline"
                        onClick={() => onAction('canary')}
                    >
                        <FlaskConical className="mr-1 h-3.5 w-3.5" /> Canary
                    </Button>
                    <Button size="sm" onClick={() => onAction('activate')}>
                        <ShieldCheck className="mr-1 h-3.5 w-3.5" /> Activate
                    </Button>
                </React.Fragment>
            )}
            {pipeline.status === 'active' && (
                <React.Fragment>
                    <Button
                        size="sm"
                        variant="outline"
                        onClick={() => onAction('run-now')}
                    >
                        <Play className="mr-1 h-3.5 w-3.5" /> Run now
                    </Button>
                    <Button
                        size="sm"
                        variant="outline"
                        onClick={() => onAction('pause')}
                    >
                        <Pause className="mr-1 h-3.5 w-3.5" /> Pause
                    </Button>
                </React.Fragment>
            )}
            {pipeline.status === 'paused' && (
                <Button size="sm" onClick={() => onAction('resume')}>
                    <RotateCcw className="mr-1 h-3.5 w-3.5" /> Resume
                </Button>
            )}
        </div>
    );
}

function RunHistory({ pipeline }: { readonly pipeline: Pipeline }) {
    const [selectedRunId, setSelectedRunId] = useState<string | null>(null);
    const { data, isLoading } = useDocumentIngestionRuns(pipeline.id);
    const { data: detail } = useDocumentIngestionRun(
        pipeline.id,
        selectedRunId,
    );
    const runs = data?.runs ?? [];

    useEffect(() => {
        setSelectedRunId(null);
    }, [pipeline.id]);

    return (
        <Card>
            <CardHeader>
                <CardTitle>
                    Run history · {pipeline.organization_name}
                </CardTitle>
                <CardDescription>
                    Sanitized operational outcomes only. This ledger never
                    stores filenames, document contents, OCR text, or S3 object
                    keys.
                </CardDescription>
            </CardHeader>
            <CardContent className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_minmax(360px,0.7fr)]">
                <div className="overflow-hidden rounded-md border">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Queued</TableHead>
                                <TableHead>Trigger</TableHead>
                                <TableHead>Status</TableHead>
                                <TableHead>Outcome</TableHead>
                                <TableHead className="w-8" />
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {isLoading && (
                                <TableRow>
                                    <TableCell
                                        colSpan={5}
                                        className="text-center"
                                    >
                                        <Spinner className="mx-auto" />
                                    </TableCell>
                                </TableRow>
                            )}
                            {!isLoading && runs.length === 0 && (
                                <TableRow>
                                    <TableCell
                                        colSpan={5}
                                        className="text-muted-foreground text-center"
                                    >
                                        No runs yet
                                    </TableCell>
                                </TableRow>
                            )}
                            {runs.map((run) => (
                                <TableRow
                                    key={run.id}
                                    className="cursor-pointer"
                                    data-state={
                                        run.id === selectedRunId
                                            ? 'selected'
                                            : undefined
                                    }
                                    onClick={() => setSelectedRunId(run.id)}
                                >
                                    <TableCell>
                                        {relativeTime(run.queued_at)}
                                    </TableCell>
                                    <TableCell className="capitalize">
                                        {run.trigger}
                                    </TableCell>
                                    <TableCell>
                                        <Badge
                                            variant={RUN_VARIANTS[run.status]}
                                        >
                                            {run.status}
                                        </Badge>
                                    </TableCell>
                                    <TableCell className="text-muted-foreground text-xs">
                                        {runSummary(run)}
                                    </TableCell>
                                    <TableCell>
                                        <ChevronRight className="text-muted-foreground h-4 w-4" />
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
                <div className="rounded-md border p-4">
                    {!selectedRunId && (
                        <p className="text-muted-foreground text-sm">
                            Select a run to inspect its safe per-document
                            outcomes.
                        </p>
                    )}
                    {selectedRunId && !detail && (
                        <Spinner className="mx-auto" />
                    )}
                    {detail && (
                        <div className="grid gap-4">
                            <div>
                                <div className="flex items-center gap-2">
                                    <Badge
                                        variant={
                                            RUN_VARIANTS[detail.run.status]
                                        }
                                    >
                                        {detail.run.status}
                                    </Badge>
                                    <span className="text-sm font-medium capitalize">
                                        {detail.run.trigger} · v
                                        {detail.run.pipeline_version}
                                    </span>
                                </div>
                                {detail.run.error_message && (
                                    <p className="text-destructive mt-2 text-sm">
                                        {detail.run.error_message}
                                    </p>
                                )}
                            </div>
                            <div className="grid grid-cols-3 gap-2 text-center text-sm">
                                <div className="bg-muted rounded-md p-2">
                                    <div className="font-semibold">
                                        {detail.run.selected_count}
                                    </div>
                                    <div className="text-muted-foreground text-xs">
                                        selected
                                    </div>
                                </div>
                                <div className="bg-muted rounded-md p-2">
                                    <div className="font-semibold">
                                        {detail.run.written_count}
                                    </div>
                                    <div className="text-muted-foreground text-xs">
                                        written
                                    </div>
                                </div>
                                <div className="bg-muted rounded-md p-2">
                                    <div className="font-semibold">
                                        {detail.run.failed_count +
                                            detail.run.refused_count}
                                    </div>
                                    <div className="text-muted-foreground text-xs">
                                        refused / failed
                                    </div>
                                </div>
                            </div>
                            <div className="max-h-72 overflow-auto rounded-md border">
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Source client</TableHead>
                                            <TableHead>Outcome</TableHead>
                                            <TableHead>Code</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {detail.attempts.map((attempt) => (
                                            <TableRow key={attempt.id}>
                                                <TableCell className="font-mono text-xs">
                                                    {attempt.external_client_id}
                                                </TableCell>
                                                <TableCell>
                                                    <Badge variant="outline">
                                                        {attempt.outcome}
                                                    </Badge>
                                                </TableCell>
                                                <TableCell className="text-muted-foreground text-xs">
                                                    {attempt.detail_code ?? '—'}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                        {detail.attempts.length === 0 && (
                                            <TableRow>
                                                <TableCell
                                                    colSpan={3}
                                                    className="text-muted-foreground text-center"
                                                >
                                                    No attempts recorded yet
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </div>
                        </div>
                    )}
                </div>
            </CardContent>
        </Card>
    );
}

function DocumentIngestionPage() {
    const [formDialog, setFormDialog] = useState<FormDialogState>(null);
    const [actionDialog, setActionDialog] = useState<ActionDialogState>(null);
    const [selectedPipelineId, setSelectedPipelineId] = useState<string | null>(
        null,
    );
    const { data: catalog } = useDocumentIngestionCatalog();
    const { data, error, isLoading, mutate } = useDocumentIngestionPipelines();
    const pipelines = useMemo(() => data?.pipelines ?? [], [data?.pipelines]);
    const sourceAccounts = data?.source_accounts ?? [];
    const selectedPipeline = pipelines.find(
        (pipeline) => pipeline.id === selectedPipelineId,
    );
    const counts = useMemo(
        () => ({
            active: pipelines.filter((pipeline) => pipeline.status === 'active')
                .length,
            attention: pipelines.filter(
                (pipeline) =>
                    pipeline.last_run?.status === 'failed' ||
                    pipeline.last_run?.status === 'partial' ||
                    !pipeline.source_account_sync_enabled,
            ).length,
            draft: pipelines.filter((pipeline) => pipeline.status === 'draft')
                .length,
        }),
        [pipelines],
    );

    const refreshAfterAction = async (pipelineId: string) => {
        setSelectedPipelineId(pipelineId);
        await mutate();
    };

    if (isLoading) {
        return (
            <div className="flex h-full items-center justify-center">
                <Loader2 className="h-6 w-6 animate-spin" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="p-6">
                <p className="text-destructive">
                    {getErrorMessage(
                        error,
                        'Unable to load document ingestion',
                    )}
                </p>
            </div>
        );
    }

    return (
        <div className="flex min-h-full flex-col gap-6 p-4 sm:p-6">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                <div>
                    <div className="flex items-center gap-2">
                        <Database className="h-5 w-5" />
                        <h1 className="text-2xl font-bold">
                            Document ingestion
                        </h1>
                        <Badge variant="outline">Internal · live only</Badge>
                    </div>
                    <p className="text-muted-foreground mt-2 max-w-3xl text-sm">
                        Configure and observe document acquisition, Textract
                        processing, identity verification, and care-plan
                        supplement writes across Phoebe organizations.
                    </p>
                </div>
                <Button onClick={() => setFormDialog({ kind: 'create' })}>
                    <Plus className="mr-2 h-4 w-4" /> Create pipeline
                </Button>
            </div>
            <div className="grid gap-3 sm:grid-cols-3">
                <Card className="gap-2 py-4">
                    <CardHeader className="px-4">
                        <CardDescription>Active schedules</CardDescription>
                        <CardTitle className="text-2xl">
                            {counts.active}
                        </CardTitle>
                    </CardHeader>
                </Card>
                <Card className="gap-2 py-4">
                    <CardHeader className="px-4">
                        <CardDescription>
                            Drafts awaiting rollout
                        </CardDescription>
                        <CardTitle className="text-2xl">
                            {counts.draft}
                        </CardTitle>
                    </CardHeader>
                </Card>
                <Card className="gap-2 py-4">
                    <CardHeader className="px-4">
                        <CardDescription>Need attention</CardDescription>
                        <CardTitle className="text-2xl">
                            {counts.attention}
                        </CardTitle>
                    </CardHeader>
                </Card>
            </div>
            <Card className="gap-4">
                <CardHeader>
                    <CardTitle>Current supported path</CardTitle>
                    <CardDescription>
                        The capability catalog is intentionally typed. New
                        source, selector, processor, identity, and destination
                        adapters can be added without making existing org
                        configurations ambiguous.
                    </CardDescription>
                </CardHeader>
                <CardContent className="grid gap-3 md:grid-cols-4">
                    {[
                        ['1 · Source', 'Explicit source account'],
                        ['2 · Select', 'Active scope + newest eligible PDF'],
                        ['3 · Process', 'PDF validation + Amazon Textract'],
                        [
                            '4 · Route',
                            'Patient identity veto + care-plan supplement',
                        ],
                    ].map(([label, value]) => (
                        <div key={label} className="bg-muted rounded-md p-3">
                            <div className="text-muted-foreground text-xs font-medium uppercase">
                                {label}
                            </div>
                            <div className="mt-1 text-sm font-medium">
                                {value}
                            </div>
                        </div>
                    ))}
                </CardContent>
            </Card>
            <div className="overflow-x-auto rounded-md border">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>Organization / source</TableHead>
                            <TableHead>Selection</TableHead>
                            <TableHead>Schedule</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead>Last run</TableHead>
                            <TableHead className="text-right">
                                Controls
                            </TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {pipelines.length === 0 && (
                            <TableRow>
                                <TableCell
                                    colSpan={6}
                                    className="text-muted-foreground h-28 text-center"
                                >
                                    No document pipelines configured yet.
                                </TableCell>
                            </TableRow>
                        )}
                        {pipelines.map((pipeline) => (
                            <TableRow
                                key={pipeline.id}
                                data-state={
                                    pipeline.id === selectedPipelineId
                                        ? 'selected'
                                        : undefined
                                }
                            >
                                <TableCell>
                                    <button
                                        type="button"
                                        className="text-left"
                                        onClick={() =>
                                            setSelectedPipelineId(pipeline.id)
                                        }
                                    >
                                        <div className="font-medium">
                                            {pipeline.organization_name}
                                        </div>
                                        <div className="text-muted-foreground text-xs">
                                            {pipeline.source_type === 'wellsky'
                                                ? 'WellSky'
                                                : 'AxisCare'}{' '}
                                            · {pipeline.source_label}
                                        </div>
                                        {!pipeline.source_account_sync_enabled && (
                                            <div className="text-destructive mt-1 text-xs">
                                                Source account sync is paused
                                            </div>
                                        )}
                                    </button>
                                </TableCell>
                                <TableCell>
                                    <div className="text-sm">
                                        {scopeLabel(pipeline)}
                                    </div>
                                    <div className="text-muted-foreground text-xs">
                                        {selectorLabel(pipeline)}
                                    </div>
                                </TableCell>
                                <TableCell>
                                    <div className="text-sm">
                                        {pipeline.schedule.type === 'weekly'
                                            ? `Every ${['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'][pipeline.schedule.weekday]}`
                                            : 'Daily'}{' '}
                                        {String(
                                            pipeline.schedule.hour,
                                        ).padStart(2, '0')}
                                        :
                                        {String(
                                            pipeline.schedule.minute,
                                        ).padStart(2, '0')}{' '}
                                        UTC
                                    </div>
                                    <div className="text-muted-foreground text-xs">
                                        cap {pipeline.max_documents_per_run} ·
                                        next{' '}
                                        {relativeTime(pipeline.next_run_at)}
                                    </div>
                                </TableCell>
                                <TableCell>
                                    <Badge
                                        variant={
                                            STATUS_VARIANTS[pipeline.status]
                                        }
                                    >
                                        {pipeline.status}
                                    </Badge>
                                </TableCell>
                                <TableCell>
                                    {pipeline.last_run ? (
                                        <button
                                            type="button"
                                            className="text-left"
                                            onClick={() =>
                                                setSelectedPipelineId(
                                                    pipeline.id,
                                                )
                                            }
                                        >
                                            <div className="flex items-center gap-2">
                                                <Badge
                                                    variant={
                                                        RUN_VARIANTS[
                                                            pipeline.last_run
                                                                .status
                                                        ]
                                                    }
                                                >
                                                    {pipeline.last_run.status}
                                                </Badge>
                                                <span className="text-xs capitalize">
                                                    {pipeline.last_run.trigger}
                                                </span>
                                            </div>
                                            <div className="text-muted-foreground mt-1 text-xs">
                                                {relativeTime(
                                                    pipeline.last_run.queued_at,
                                                )}
                                            </div>
                                        </button>
                                    ) : (
                                        <span className="text-muted-foreground text-sm">
                                            Never
                                        </span>
                                    )}
                                </TableCell>
                                <TableCell>
                                    <PipelineActions
                                        pipeline={pipeline}
                                        onEdit={() =>
                                            setFormDialog({
                                                kind: 'edit',
                                                pipeline,
                                            })
                                        }
                                        onAction={(action) =>
                                            setActionDialog({
                                                action,
                                                pipeline,
                                            })
                                        }
                                    />
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </div>
            {selectedPipeline && <RunHistory pipeline={selectedPipeline} />}
            <div className="bg-muted/40 text-muted-foreground flex gap-3 rounded-md border p-4 text-sm">
                <ShieldCheck className="text-foreground mt-0.5 h-4 w-4 shrink-0" />
                <p>
                    Pipeline configuration is reusable per organization and
                    source account. Versioned run snapshots, source-hash
                    deduplication, PDF validation, identity refusal, and
                    transactional writes keep retries safe. Pausing acquisition
                    does not remove supplements already used by Phoebe.
                </p>
            </div>
            <PipelineFormDialog
                state={formDialog}
                sourceAccounts={sourceAccounts}
                minimumCap={catalog?.min_documents_per_run ?? 1}
                maximumCap={catalog?.max_documents_per_run ?? 100}
                onClose={() => setFormDialog(null)}
                onSaved={mutate}
            />
            <ActionDialog
                state={actionDialog}
                onClose={() => setActionDialog(null)}
                onComplete={refreshAfterAction}
            />
        </div>
    );
}
